import { useState } from "react";
import { Form, Formik } from "formik";
import useScripts from "../../hooks/use-scripts";
import Card from "../UI/Card";
import Button from "../UI/Button";
import TextInput from "../UI/TextInput";
import styles from "./Scripts.module.css";

export default function Scripts() {
  const { scripts, isLoading, error, runScript } = useScripts();
  const [selectedScript, setSelectedScript] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);

  if (error) {
    return (
      <Card>
        <p className={styles.error}>
          Erreur lors du chargement des scripts: {error.message}
        </p>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <p>Chargement des scripts...</p>
      </Card>
    );
  }

  const scriptModules = Object.keys(scripts).sort();

  const handleScriptSelect = (moduleName, functionData) => {
    setSelectedScript({
      module: moduleName,
      function: functionData,
      fullPath: `${moduleName}.${functionData.name}`,
    });
    setExecutionResult(null);
  };

  const handleExecuteScript = async (values) => {
    setIsExecuting(true);
    setExecutionResult(null);

    try {
      const sanitizedValues = sanitizeValues(
        values,
        selectedScript.function.parameters,
      );
      const result = await runScript(selectedScript.fullPath, sanitizedValues);
      setExecutionResult(result);
    } catch (error) {
      setExecutionResult({
        status: "error",
        error: error.message || "Une erreur est survenue lors de l'exécution",
      });
    } finally {
      setIsExecuting(false);
    }
  };

  // Build initial values for Formik based on parameters
  const buildInitialValues = (parameters) => {
    const values = {};
    parameters.forEach((param) => {
      values[param.name] = param.default !== null ? param.default : "";
    });
    return values;
  };

  // Sanitize values before submission: convert empty strings back to null
  // when the original parameter default was null
  const sanitizeValues = (values, parameters) => {
    const sanitized = { ...values };
    parameters.forEach((param) => {
      if (param.default === null && sanitized[param.name] === "") {
        sanitized[param.name] = null;
      }
    });
    return sanitized;
  };

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <Card>
          <h2 className={styles.sidebarTitle}>Scripts Disponibles</h2>
          {scriptModules.length === 0 ? (
            <p className={styles.noScripts}>Aucun script disponible</p>
          ) : (
            <div className={styles.scriptsList}>
              {scriptModules.map((moduleName) => (
                <div key={moduleName} className={styles.moduleSection}>
                  <h3 className={styles.moduleName}>{moduleName}</h3>
                  <div className={styles.functionsList}>
                    {scripts[moduleName].functions.map((func) => (
                      <button
                        key={func.name}
                        className={`${styles.functionButton} ${
                          selectedScript?.fullPath ===
                          `${moduleName}.${func.name}`
                            ? styles.functionButtonActive
                            : ""
                        }`}
                        onClick={() => handleScriptSelect(moduleName, func)}
                      >
                        <span className={styles.functionName}>{func.name}</span>
                        {func.parameters.length > 0 && (
                          <span className={styles.paramCount}>
                            {func.parameters.length} param
                            {func.parameters.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className={styles.mainContent}>
        {!selectedScript ? (
          <Card>
            <div className={styles.emptyState}>
              <p>Sélectionnez un script dans la liste pour commencer</p>
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <div className={styles.scriptHeader}>
                <h2 className={styles.scriptTitle}>
                  {selectedScript.fullPath}
                </h2>
                {selectedScript.function.description && (
                  <p className={styles.scriptDescription}>
                    {selectedScript.function.description}
                  </p>
                )}
              </div>

              {selectedScript.function.parameters.length === 0 ? (
                <div className={styles.noParams}>
                  <p>Ce script ne nécessite aucun paramètre</p>
                  <Button
                    onClick={() => handleExecuteScript({})}
                    disabled={isExecuting}
                    className={styles.executeButton}
                  >
                    {isExecuting ? "Exécution en cours..." : "Exécuter"}
                  </Button>
                </div>
              ) : (
                <Formik
                  initialValues={buildInitialValues(
                    selectedScript.function.parameters,
                  )}
                  onSubmit={handleExecuteScript}
                  enableReinitialize
                >
                  {() => (
                    <Form className={styles.paramsForm}>
                      <div className={styles.paramsSection}>
                        <h3 className={styles.sectionTitle}>Paramètres</h3>
                        <div className={styles.paramsList}>
                          {selectedScript.function.parameters.map((param) => (
                            <div key={param.name} className={styles.paramField}>
                              <TextInput
                                name={param.name}
                                label={param.name}
                                placeholder={
                                  param.default
                                    ? `Défaut: ${param.default}`
                                    : "Valeur"
                                }
                                required={param.required}
                              />
                              {param.required && (
                                <span className={styles.requiredBadge}>
                                  Requis
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={isExecuting}
                        className={styles.executeButton}
                      >
                        {isExecuting ? "Exécution en cours..." : "Exécuter"}
                      </Button>
                    </Form>
                  )}
                </Formik>
              )}
            </Card>

            {executionResult && (
              <Card className={styles.resultCard}>
                <h3 className={styles.resultTitle}>Résultat</h3>
                <div
                  className={`${styles.resultContent} ${
                    executionResult.status === "success"
                      ? styles.resultSuccess
                      : styles.resultError
                  }`}
                >
                  <div className={styles.statusBadge}>
                    <span
                      className={
                        executionResult.status === "success"
                          ? styles.statusSuccess
                          : styles.statusError
                      }
                    >
                      {executionResult.status === "success"
                        ? "✓ Succès"
                        : "✗ Erreur"}
                    </span>
                  </div>

                  {executionResult.status === "success" ? (
                    <div className={styles.successContent}>
                      <p className={styles.successMessage}>
                        Le script s'est exécuté avec succès
                      </p>
                      {executionResult.result && (
                        <div className={styles.resultData}>
                          <strong>Résultat:</strong>
                          <pre className={styles.resultPre}>
                            {executionResult.result}
                          </pre>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.errorContent}>
                      <p className={styles.errorMessage}>
                        <strong>Erreur:</strong> {executionResult.error}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
